/**
 * The organization's library of MCP servers, skills and plugins, and its
 * allowlist (`policy.extensions`, `org/extension-library.ts` in contracts),
 * under the All agents page's rows.
 *
 * The lists are edited with the same field a workspace's own list is
 * (`ExtensionsField`) — the entries take the same shapes, and the library is
 * what a member adds to a workspace from. What the field has no column for is
 * each MCP server's **organization secrets**: names only, never a value, one
 * line per server below the lists. Policy, like the network lists: nothing on
 * a member's machine sits beneath it, so there is no padlock — the allowlist
 * switch is the decision.
 */
import { useState } from 'react';
import type React from 'react';
import type { OrgExtensions } from '@airlock/contracts/org/extension-library';
import { EMPTY_ORG_POLICY, policyOf, type OrgPolicy } from '@airlock/contracts/org/policy';
import { ORG_SECRET_NAME } from '@airlock/contracts/cloud/org-secrets';
import type { AgentExtensions } from '@airlock/contracts/settings/extensions';
import { color, font } from '../../theme/tokens';
import { SettingToggle } from '../../components/settings-row';
import type { OrgSettings } from '../../lib/types';
import { ExtensionsField } from './ExtensionsField';
import { ListBlock } from './list-settings';

/** The library as the field edits it: every entry switched on. */
function asField(library: OrgExtensions['library']): AgentExtensions {
  return {
    mcpServers: library.mcpServers.map(({ secrets: _secrets, ...entry }) => ({ ...entry, enabled: true })),
    skills: library.skills.map((entry) => ({ ...entry, enabled: true })),
    plugins: library.plugins.map((entry) => ({ ...entry, enabled: true }))
  };
}

export function ExtensionLibraryPolicy({
  settings,
  editable,
  onChange
}: {
  settings: OrgSettings;
  editable: boolean;
  onChange: (next: OrgPolicy) => void;
}): React.JSX.Element {
  const policy = policyOf(settings);
  // The draft as typed, not as a machine would read it: `policyOf` drops an
  // entry still being written (a server with no command yet), which would take
  // the row away under the cursor. The Worker's validator says what is wrong.
  const drafted = (settings.policy as { extensions?: OrgExtensions } | undefined)?.extensions;
  const extensions = drafted?.library ? drafted : policy.extensions;
  const [typed, setTyped] = useState<Record<string, string>>({});
  const put = (next: OrgExtensions): void => onChange({ ...EMPTY_ORG_POLICY, ...policy, extensions: next });

  // Back from the field: what it kept, each server keeping the secrets' names it had.
  const fromField = (doc: AgentExtensions): void => {
    const secretsOf = (name: string): string[] => extensions.library.mcpServers.find((one) => one.name === name)?.secrets ?? [];
    put({
      ...extensions,
      library: {
        mcpServers: doc.mcpServers.map(({ enabled: _on, ...entry }) => ({ ...entry, secrets: secretsOf(entry.name) })),
        skills: doc.skills.map(({ enabled: _on, ...entry }) => entry),
        plugins: doc.plugins.map(({ enabled: _on, ...entry }) => entry)
      }
    });
  };
  const setSecrets = (name: string, text: string): void => {
    setTyped((current) => ({ ...current, [name]: text }));
    const names = text
      .split(/[\s,]+/)
      .map((one) => one.trim())
      .filter((one) => ORG_SECRET_NAME.test(one));
    put({
      ...extensions,
      library: { ...extensions.library, mcpServers: extensions.library.mcpServers.map((one) => (one.name === name ? { ...one, secrets: names } : one)) }
    });
  };

  return (
    <>
      <ListBlock
        label="Library of MCP servers, skills and plugins"
        hint="Approved for your members: each one is offered on a workspace’s Skills, MCP & plugins page, and switched on there. A server that needs a token names the organization secrets it reads below — their values reach a session like every organization secret, never through this list."
      >
        <ExtensionsField value={asField(extensions.library)} onChange={fromField} editable={editable} />
        {extensions.library.mcpServers.map((server) => (
          <div key={server.name} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <span style={{ fontFamily: font.mono, fontSize: 12.5, width: 160, color: color.ink }}>{server.name}</span>
            {editable ? (
              <input
                value={typed[server.name] ?? server.secrets.join(', ')}
                placeholder="Organization secrets it reads, e.g. ACME_GITHUB_TOKEN"
                onChange={(event) => setSecrets(server.name, event.target.value)}
                style={{ flex: 1, maxWidth: 420, height: 30, padding: '0 10px', fontFamily: font.mono, fontSize: 12.5 }}
              />
            ) : (
              <span style={{ fontSize: 12.5, color: color.inkMuted }}>{server.secrets.length ? `reads ${server.secrets.join(', ')}` : 'reads no organization secret'}</span>
            )}
          </div>
        ))}
      </ListBlock>
      <ListBlock
        label="Allow only the library"
        hint="On, a session whose agent would be given an MCP server, a skill or a plugin that is not in the library — exactly as written here — is refused before it starts, on every member’s machine and every workspace, naming your organization."
      >
        {editable ? (
          <SettingToggle on={extensions.allowlist} onToggle={() => put({ ...extensions, allowlist: !extensions.allowlist })} />
        ) : (
          <p style={{ margin: 0, fontSize: 13, color: color.inkMuted }}>{extensions.allowlist ? 'Only the library.' : 'Anything a workspace adds.'}</p>
        )}
      </ListBlock>
    </>
  );
}
